import { MagnifyingGlass, Plus } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { ASSETS } from '../data/assets';
import type { AssetCategory, AssetDefinition } from '../types';
import { LazyAssetThumbnail } from './LazyAssetThumbnail';
import { Modal } from './Modal';

const categories: AssetCategory[] = ['전체', '가구', '디스플레이', '부스 구조', '장식'];

interface StackModalProps {
  open: boolean;
  supportName: string;
  onClose: () => void;
  onChoose: (asset: AssetDefinition) => void;
}

export function StackModal({ open, supportName, onClose, onChoose }: StackModalProps) {
  const [category, setCategory] = useState<AssetCategory>('전체');
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!open) return;
    setCategory('전체');
    setSearch('');
  }, [open]);
  const filtered = useMemo(() => ASSETS.filter((asset) => {
    const categoryMatches = category === '전체' || asset.category === category;
    const query = search.trim().toLowerCase();
    return categoryMatches && (!query || `${asset.name} ${asset.description}`.toLowerCase().includes(query));
  }), [category, search]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="위에 올릴 오브젝트"
      description={`${supportName} 상단 중앙에 실측 크기 그대로 배치합니다.`}
      className="stack-modal"
    >
      <div className="stack-toolbar">
        <label className="stack-search">
          <MagnifyingGlass size={16} aria-hidden="true" />
          <span className="sr-only">올릴 오브젝트 검색</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="이름 또는 크기로 검색" autoFocus />
        </label>
        <div className="stack-categories" role="tablist" aria-label="오브젝트 분류">
          {categories.map((item) => (
            <button key={item} type="button" role="tab" aria-selected={category === item} className={category === item ? 'active' : ''} onClick={() => setCategory(item)}>{item}</button>
          ))}
        </div>
      </div>
      <div className="stack-result-meta"><strong>{filtered.length}</strong>개 오브젝트</div>
      <div className="stack-asset-grid">
        {filtered.map((asset) => (
          <button key={asset.kind} type="button" onClick={() => onChoose(asset)}>
            <LazyAssetThumbnail asset={asset} className="stack-asset-preview" />
            <span className="stack-asset-copy">
              <strong>{asset.name}</strong>
              <small>{asset.description}</small>
            </span>
            <Plus size={15} weight="bold" aria-hidden="true" />
          </button>
        ))}
        {!filtered.length && <p className="stack-empty">조건에 맞는 오브젝트가 없습니다.</p>}
      </div>
      <p className="stack-modal-help">받침을 이동하거나 삭제하면 위에 쌓인 오브젝트도 함께 반영됩니다.</p>
    </Modal>
  );
}
